import { useEffect, useRef, useState } from 'react';
import { Search, Upload } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useEmpresas, useEmpresaDocumentos, useEnviarDocumentoEmpresa } from '@/hooks/useEmpresa';
import { useLicitacoesFeed } from '@/hooks/useLicitacoes';

export interface AgenteIaContexto {
  empresaId: number;
  licitacaoId: number;
  documentoId: number;
}

interface AgenteIaContextoSeletorProps {
  onContextoCompleto: (contexto: AgenteIaContexto | null) => void;
  initialEmpresaId?: number;
  initialLicitacaoId?: number;
  contextoTravado?: boolean;
  empresaRestritaId?: number;
}

const selectClassName =
  'flex w-full rounded-lg border border-ds-border bg-ds-surface2 px-3 py-2.5 text-sm text-ds-text-primary ' +
  'focus:outline-none focus:ring-2 focus:ring-ds-brand/50 focus:border-ds-brand/50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200';

function AgenteIaContextoSeletor({ onContextoCompleto, initialEmpresaId, initialLicitacaoId, contextoTravado = false, empresaRestritaId }: AgenteIaContextoSeletorProps) {
  const resolvedEmpresaId = empresaRestritaId || initialEmpresaId;
  const [empresaId, setEmpresaId] = useState<string>(resolvedEmpresaId ? String(resolvedEmpresaId) : '');
  const [licitacaoSearch, setLicitacaoSearch] = useState('');
  const [licitacaoId, setLicitacaoId] = useState<string>(initialLicitacaoId ? String(initialLicitacaoId) : '');
  const [documentoId, setDocumentoId] = useState<string>('');
  const [categoriaUpload, setCategoriaUpload] = useState<'edital' | 'termo_referencia'>('edital');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const { data: empresas, isLoading: empresasLoading } = useEmpresas();
  const { data: licitacoesFeed, isLoading: licitacoesLoading } = useLicitacoesFeed({
    search: licitacaoSearch || undefined,
    limit: 20,
  });
  const { data: documentos, isLoading: documentosLoading } = useEmpresaDocumentos(empresaId || undefined);
  const enviarDocumento = useEnviarDocumentoEmpresa(empresaId || undefined);

  const licitacoes = licitacoesFeed?.licitacoes || [];
  const empresaAtual = (empresas || []).find((empresa) => empresa.id === Number(empresaId));
  const licitacaoAtual = licitacoes.find((licitacao) => licitacao.id === Number(licitacaoId));
  // A ação parte de uma licitação já validada no servidor. Ela pode não estar
  // na primeira página do feed local; nesse caso não devemos bloquear o fluxo
  // nem trocar o processo por um item arbitrário do feed.
  const licitacaoConfirmada = Boolean(licitacaoAtual) || (
    Boolean(initialLicitacaoId) && Number(licitacaoId) === initialLicitacaoId
  );
  const documentosAtivos = (documentos || []).filter((documento) => documento.is_active !== false);
  const documentoProcessado = (documento: { processing_status?: string | null }) =>
    ['disponivel', 'verificacao_manual'].includes(documento.processing_status || '');
  const referenciasLicitacao = documentosAtivos.filter((documento) =>
    ['edital', 'termo_referencia'].includes(documento.categoria) && documentoProcessado(documento)
  );
  const documentosComConteudo = documentosAtivos.filter((documento) =>
    documentoProcessado(documento)
  );

  useEffect(() => {
    if (resolvedEmpresaId && initialLicitacaoId && empresaId === String(resolvedEmpresaId)) return;
    setLicitacaoId('');
    setDocumentoId('');
  }, [empresaId, resolvedEmpresaId, initialLicitacaoId]);

  useEffect(() => {
    const empresaIdNum = parseInt(empresaId, 10);
    const licitacaoIdNum = parseInt(licitacaoId, 10);
    const documentoIdNum = parseInt(documentoId, 10);

    if (empresaIdNum && licitacaoIdNum && documentoIdNum && licitacaoConfirmada) {
      onContextoCompleto({ empresaId: empresaIdNum, licitacaoId: licitacaoIdNum, documentoId: documentoIdNum });
    } else {
      onContextoCompleto(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, licitacaoId, documentoId, licitacaoConfirmada]);

  const enviarReferencia = async (file: File) => {
    setUploadError(null);
    setUploadStatus(null);
    try {
      const documento = await enviarDocumento.mutateAsync({ arquivo: file, categoria: categoriaUpload });
      if (documentoProcessado(documento)) {
        setDocumentoId(String(documento.id));
        setUploadStatus(
          documento.processing_status === 'verificacao_manual'
            ? 'Arquivo selecionado. A leitura requer conferência humana antes da geração.'
            : 'Arquivo processado e selecionado como referência para este rascunho.'
        );
      } else {
        setUploadError('O arquivo foi enviado, mas ainda não pôde ser processado. Confira o formato e tente enviá-lo novamente.');
      }
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Não foi possível enviar o arquivo. Tente novamente.');
    } finally {
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    }
  };

  const referenciaUpload = (
    <div className="mt-3 rounded-lg border border-ds-warning/30 bg-ds-warning/10 p-3">
      <p className="text-sm font-medium text-ds-text-primary">Envie o edital ou termo de referência desta licitação</p>
      <p className="mt-1 text-sm text-ds-text-secondary">O arquivo será processado antes de ficar disponível para o rascunho. Os demais documentos ativos da empresa continuam como evidência.</p>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="text-sm text-ds-text-secondary" htmlFor="agente-ia-categoria-upload">
          Classificar como
          <select id="agente-ia-categoria-upload" className={`${selectClassName} mt-1 min-w-52`} value={categoriaUpload} onChange={(event) => setCategoriaUpload(event.target.value as 'edital' | 'termo_referencia')} disabled={enviarDocumento.isPending}>
            <option value="edital">Edital</option>
            <option value="termo_referencia">Termo de referência</option>
          </select>
        </label>
        <Button type="button" size="sm" variant="secondary" disabled={enviarDocumento.isPending} onClick={() => uploadInputRef.current?.click()}>
          <Upload className="mr-2 h-4 w-4" />{enviarDocumento.isPending ? 'Enviando e processando...' : 'Enviar arquivo'}
        </Button>
      </div>
      <input ref={uploadInputRef} className="sr-only" aria-label="Arquivo do edital ou termo de referência" type="file" accept="application/pdf,image/png,image/jpeg" onChange={(event) => {
        const file = event.target.files?.[0];
        if (file) void enviarReferencia(file);
      }} />
      {uploadError && <p className="mt-2 text-sm text-ds-danger" role="alert">{uploadError}</p>}
      {uploadStatus && <p className="mt-2 text-sm text-ds-success" role="status">{uploadStatus}</p>}
    </div>
  );

  return (
    <Card>
      {contextoTravado ? (
        <div className="space-y-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-ds-text-secondary">Você está trabalhando em</p>
            <p className="mt-1 font-semibold text-ds-text-primary">{licitacaoAtual?.objeto || 'Licitação selecionada'}</p>
            <p className="text-sm text-ds-text-secondary">{empresaAtual?.razao_social || 'Empresa selecionada'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-ds-text-secondary mb-2" htmlFor="agente-ia-documento">Edital ou termo de referência</label>
            <select id="agente-ia-documento" className={selectClassName} value={documentoId} onChange={(e) => setDocumentoId(e.target.value)} disabled={documentosLoading}>
              <option value="">Selecione o edital ou termo desta licitação</option>
              {referenciasLicitacao.map((documento) => <option key={documento.id} value={documento.id}>{documento.nome_arquivo}</option>)}
            </select>
            {!documentosLoading && !referenciasLicitacao.length && referenciaUpload}
            {!!documentosAtivos.length && <p className="mt-2 text-sm text-ds-text-secondary">O arquivo selecionado será a base do edital. Os {documentosAtivos.length} documento(s) ativo(s) da empresa serão considerados como evidência; {documentosComConteudo.length} já possuem conteúdo disponível para leitura.</p>}
            {!licitacaoConfirmada && <p className="mt-2 text-sm text-ds-warning">Não foi possível confirmar esta licitação. Volte às licitações e abra o processo novamente.</p>}
          </div>
        </div>
      ) : (
      <div className={`grid grid-cols-1 gap-4 ${empresaRestritaId ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
        {!empresaRestritaId && <div>
          <label className="block text-sm font-medium text-ds-text-secondary mb-2" htmlFor="agente-ia-empresa">
            Empresa
          </label>
          <select
            id="agente-ia-empresa"
            className={selectClassName}
            value={empresaId}
            onChange={(e) => setEmpresaId(e.target.value)}
            disabled={empresasLoading}
          >
            <option value="">Selecione uma empresa</option>
            {(empresas || []).map((empresa) => (
              <option key={empresa.id} value={empresa.id}>
                {empresa.razao_social}
              </option>
            ))}
          </select>
        </div>}

        <div>
          <label className="block text-sm font-medium text-ds-text-secondary mb-2" htmlFor="agente-ia-licitacao">
            Licitação
          </label>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ds-text-secondary" />
            <Input
              placeholder="Buscar por objeto, órgão..."
              value={licitacaoSearch}
              onChange={(e) => setLicitacaoSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            id="agente-ia-licitacao"
            className={selectClassName}
            value={licitacaoId}
            onChange={(e) => setLicitacaoId(e.target.value)}
            disabled={licitacoesLoading}
          >
            <option value="">Selecione uma licitação</option>
            {initialLicitacaoId && !licitacoes.some((licitacao) => licitacao.id === initialLicitacaoId) && (
              <option value={initialLicitacaoId}>Licitação selecionada</option>
            )}
            {licitacoes.map((licitacao) => (
              <option key={licitacao.id} value={licitacao.id}>
                {licitacao.numero_edital ? `${licitacao.numero_edital} — ` : ''}
                {licitacao.objeto}
                {licitacao.orgao ? ` (${licitacao.orgao})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-ds-text-secondary mb-2" htmlFor="agente-ia-documento">
            Edital ou termo de referência
          </label>
          <select
            id="agente-ia-documento"
            className={selectClassName}
            value={documentoId}
            onChange={(e) => setDocumentoId(e.target.value)}
            disabled={!empresaId || documentosLoading}
          >
            <option value="">
              {empresaId ? 'Selecione o edital ou termo' : 'Selecione uma empresa primeiro'}
            </option>
            {referenciasLicitacao.map((documento) => (
              <option key={documento.id} value={documento.id}>
                {documento.nome_arquivo}
              </option>
            ))}
          </select>
          {!!empresaId && !!documentosAtivos.length && <p className="mt-2 text-xs text-ds-text-secondary">O Redator também considera os demais documentos ativos e autorizados da empresa.</p>}
          {!!empresaId && !documentosLoading && !referenciasLicitacao.length && referenciaUpload}
        </div>
      </div>
      )}
    </Card>
  );
}

export { AgenteIaContextoSeletor };
